import React, { FC } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import parse from 'html-react-parser';
import { AddToCart } from '@components';
import { toCurrency } from '@helpers';
import { AvaSuburbOOSProducts, BulkPricingRule } from '@models';
import { useGetAllProductsQuery, useGetBulkPricingRulesQuery } from '@services';

type Props = {
  productId: number;
  isOpen: boolean;
  onClose: () => void;
  suburbProductsStatus?: AvaSuburbOOSProducts;
  selectedStateIndex: number;
};

export const OrderProductInfoModal: FC<Props> = ({ isOpen, onClose, productId, suburbProductsStatus, selectedStateIndex }) => {
  const { data: products } = useGetAllProductsQuery();
  const { data: bulkPricingRules, isFetching } = useGetBulkPricingRulesQuery(productId);

  const product = products?.find((prod) => prod.id === productId);
  const productIdSku = product?.sku || '';
  const actionDisabled =
    suburbProductsStatus?.oos.includes(productIdSku) ||
    suburbProductsStatus?.snoozed.includes(productIdSku) ||
    suburbProductsStatus?.deranged.includes(productIdSku);

  const getRegion = () => {
    const region = product?.custom_fields?.find((field) => field.name === 'display_region')?.value;

    // Custom fields can't be empty so those fields that shouldn't
    // have a value instead have been added with the string 'null'.
    if (region && region !== 'null') {
      return region;
    } else {
      return 'Unknown';
    }
  };

  const showBulkPricingRules = bulkPricingRules ? bulkPricingRules.length > 0 : false;

  const getBulkPricingType = () => {
    if (!bulkPricingRules || bulkPricingRules.length === 0) return '';

    if (bulkPricingRules[0].type === 'fixed') return 'Fixed Amount';

    if (bulkPricingRules[0].type === 'percent') return 'Percentage Discount';

    if (bulkPricingRules[0].type === 'price') return '$ Off/Unit';
  };

  const getRuleAmount = (rule: BulkPricingRule) => {
    if (rule.type === 'fixed' || rule.type === 'price') {
      return toCurrency(rule.amount);
    }

    if (rule.type === 'percent') {
      return `${rule.amount}%`;
    }
  };

  const getUnitPrice = (rule: BulkPricingRule) => {
    if (rule.type === 'fixed') {
      return toCurrency(rule.amount);
    }

    if (product?.price) {
      if (rule.type === 'percent') {
        return toCurrency(product.price * (1 - rule.amount / 100));
      }

      if (rule.type === 'price') {
        return toCurrency(product?.price - rule.amount);
      }
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" data-testid="order-product-info-modal">
      <DialogTitle>{product?.name}</DialogTitle>
      <DialogContent sx={{ marginTop: 2 }}>
        {isFetching ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress size={80} />
          </Box>
        ) : (
          <>
            <DialogContentText>
              Region: <strong>{getRegion()}</strong>
            </DialogContentText>
            {product?.description && <DialogContentText component="div">{parse(product.description)}</DialogContentText>}
            {showBulkPricingRules && (
              <>
                <Typography variant="h6">Bulk Pricing</Typography>
                <TableContainer component={Paper} sx={{ width: 420 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Minimum Quantity</TableCell>
                        <TableCell>{getBulkPricingType()}</TableCell>
                        <TableCell>Unit Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkPricingRules?.map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell>{rule.quantity_min}</TableCell>
                          <TableCell>{getRuleAmount(rule)}</TableCell>
                          <TableCell>{getUnitPrice(rule)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <AddToCart productId={productId} disabled={actionDisabled} selectedStateIndex={selectedStateIndex} />
      </DialogActions>
    </Dialog>
  );
};
